import React, { useState, useEffect, useMemo } from 'react';
import { useAgents } from '../hooks/useAgents';
import { useDebounce } from '../hooks/useDebounce';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { themeClasses, combineClasses } from '../utils/theme';
import { UnifiedAgent } from '../types';

/**
 * AgentManagementPage Component - Unified management for workflows and assistants
 * 
 * This component provides a single unified interface for managing all agents:
 * - View all agents (workflows and assistants) in one list
 * - Search and filter agents across all types
 * - Create new agents with unified modal
 * - Delete/unlink agents where supported
 */
const AgentManagementPage: React.FC = () => {
  // Theme context is consumed by themeClasses utility
  useTheme();
  
  // Auth context for role-based access
  const { isAdmin } = useAuth();

  // Use unified agents hook
  const {
    agents,
    isLoading,
    error,
    createAgent,
    deleteAgent,
    searchAgents,
    clearError,
    totalAgents,
    workflowCount,
    assistantCount
  } = useAgents();

  // Local state for UI
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const [filterType, setFilterType] = useState<'all' | 'workflow' | 'assistant'>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get filtered agents based on search and filter
  const filteredAgents = useMemo(() => {
    let result = debouncedSearchQuery ? searchAgents(debouncedSearchQuery) : agents;

    if (filterType !== 'all') {
      result = result.filter(agent => agent.type === filterType);
    }

    return result;
  }, [agents, debouncedSearchQuery, filterType, searchAgents]);

  const handleDeleteAgent = async (agent: UnifiedAgent) => {
    const agentTypeName = agent.type === 'workflow' ? 'workflow' : 'assistant';
    if (!window.confirm(`Are you sure you want to unlink "${agent.name}"?`)) {
      return;
    }

    try {
      clearError();
      setSuccessMessage(null);

      const result = await deleteAgent(agent);

      if (result.success) {
        setSuccessMessage(`${agentTypeName.charAt(0).toUpperCase() + agentTypeName.slice(1)} unlinked successfully!`);
      }
    } catch (err) {
      console.error('Error unlinking agent:', err);
    }
  };

  // Clear messages after a delay
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => clearError(), 8000);
      return () => clearTimeout(timer);
    }
  }, [error, clearError]);

  interface UnifiedAgentCardProps {
    agent: UnifiedAgent;
    onDelete: (agent: UnifiedAgent) => void;
  }

  const UnifiedAgentCard: React.FC<UnifiedAgentCardProps> = ({ agent, onDelete }) => (
    <div className={combineClasses(
      themeClasses.card.base,
      themeClasses.card.hover,
      'p-4'
    )}>
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <div className={combineClasses(
            'flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center',
            agent.type === 'workflow'
              ? 'bg-blue-100 dark:bg-blue-900/30'
              : 'bg-purple-100 dark:bg-purple-900/30'
          )}>
            <svg className={combineClasses(
              'w-6 h-6',
              agent.type === 'workflow'
                ? 'text-blue-600 dark:text-blue-400'
                : 'text-purple-600 dark:text-purple-400'
            )} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={
                agent.type === 'workflow'
                  ? 'M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2'
                  : 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z'
              } />
            </svg>
          </div>
          <div className="ml-4">
            <div className="flex items-center gap-2">
              <h3 className={combineClasses(
                'text-sm font-medium',
                themeClasses.text.primary
              )}>
                {agent.name}
              </h3>
              <span className={combineClasses(
                'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
                agent.type === 'workflow'
                  ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                  : 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
              )}>
                {agent.type === 'workflow' ? 'Workflow' : 'Assistant'}
              </span>
            </div>
            <p className={combineClasses(
              'text-sm',
              themeClasses.text.secondary
            )}>
              ID: {agent.vapiId}
            </p>
            <p className={combineClasses(
              'text-xs',
              themeClasses.text.muted
            )}>
              Created {agent.createdAt.toLocaleDateString()}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={combineClasses(
            'inline-flex items-center px-2 py-1 rounded-full text-xs font-medium',
            agent.status === 'active'
              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
              : 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300'
          )}>
            {agent.status}
          </span>
          <button
            onClick={() => onDelete(agent)}
            className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 text-sm font-medium transition-colors"
          >
            Unlink
          </button>
        </div>
      </div>
    </div>
  );

  // Create Agent Modal Component
  interface CreateAgentModalProps {
    isOpen: boolean;
    onClose: () => void;
  }

  const CreateAgentModal: React.FC<CreateAgentModalProps> = ({ isOpen, onClose }) => {
    const [agentType, setAgentType] = useState<'workflow' | 'assistant'>('workflow');
    const [vapiId, setVapiId] = useState('');
    const [agentName, setAgentName] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();

      if (!vapiId.trim()) {
        return;
      }

      try {
        setIsSubmitting(true);
        clearError();
        setSuccessMessage(null);

        const result = await createAgent({
          type: agentType,
          vapiId: vapiId.trim(),
          name: agentName.trim() || undefined
        });

        if (result.success) {
          setSuccessMessage(`${agentType.charAt(0).toUpperCase() + agentType.slice(1)} linked successfully!`);
          setVapiId('');
          setAgentName('');
          onClose();
        }
      } catch (err) {
        console.error('Error creating agent:', err);
      } finally {
        setIsSubmitting(false);
      }
    };

    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className={combineClasses(
          themeClasses.card.base,
          'max-w-md w-full max-h-[90vh] overflow-y-auto'
        )}>
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className={combineClasses(
                'text-lg font-medium',
                themeClasses.text.primary
              )}>
                Create New Agent
              </h3>
              <button
                onClick={onClose}
                className={combineClasses(
                  'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300',
                  themeClasses.text.secondary
                )}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className={combineClasses(
                  'block text-sm font-medium mb-2',
                  themeClasses.text.primary
                )}>
                  Agent Type
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="workflow"
                      checked={agentType === 'workflow'}
                      onChange={(e) => setAgentType(e.target.value as 'workflow' | 'assistant')}
                      className="mr-2"
                    />
                    <span className={themeClasses.text.primary}>Workflow</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="assistant"
                      checked={agentType === 'assistant'}
                      onChange={(e) => setAgentType(e.target.value as 'workflow' | 'assistant')}
                      className="mr-2"
                    />
                    <span className={themeClasses.text.primary}>Assistant</span>
                  </label>
                </div>
              </div>

              <div>
                <label htmlFor="agent-id" className={combineClasses(
                  'block text-sm font-medium',
                  themeClasses.text.primary
                )}>
                  {agentType.charAt(0).toUpperCase() + agentType.slice(1)} ID *
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    id="agent-id"
                    value={vapiId}
                    onChange={(e) => setVapiId(e.target.value)}
                    placeholder={`Enter your ${agentType} ID`}
                    className={combineClasses(
                      'block w-full px-3 py-2 rounded-md shadow-sm sm:text-sm',
                      'border border-gray-300 dark:border-gray-600',
                      'bg-white dark:bg-gray-800',
                      'text-gray-900 dark:text-white',
                      'placeholder-gray-400 dark:placeholder-gray-500',
                      'focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400'
                    )}
                    disabled={isSubmitting}
                    required
                  />
                </div>
              </div>

              {agentType === 'assistant' && (
                <div>
                  <label htmlFor="agent-name" className={combineClasses(
                    'block text-sm font-medium',
                    themeClasses.text.primary
                  )}>
                    Agent Name (Optional)
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      id="agent-name"
                      value={agentName}
                      onChange={(e) => setAgentName(e.target.value)}
                      placeholder="Enter a friendly name for this agent"
                      className={combineClasses(
                        'block w-full px-3 py-2 rounded-md shadow-sm sm:text-sm',
                        'border border-gray-300 dark:border-gray-600',
                        'bg-white dark:bg-gray-800',
                        'text-gray-900 dark:text-white',
                        'placeholder-gray-400 dark:placeholder-gray-500',
                        'focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400'
                      )}
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className={combineClasses(
                    'px-4 py-2 text-sm font-medium rounded-md',
                    'border border-gray-300 dark:border-gray-600',
                    themeClasses.text.primary,
                    'hover:bg-gray-50 dark:hover:bg-gray-700'
                  )}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || !vapiId.trim()}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-blue-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isSubmitting ? 'Creating...' : 'Create Agent'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className={combineClasses(
            'text-2xl font-bold',
            themeClasses.text.primary
          )}>Agent Management</h1>
          <p className={combineClasses(
            'text-sm mt-1',
            themeClasses.text.secondary
          )}>
            Manage all your agents in one place. Total: {totalAgents} ({workflowCount} workflows, {assistantCount} assistants)
          </p>
        </div>
        {/* Create Agent button - Admin only */}
        {isAdmin && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition-colors"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create Agent
          </button>
        )}
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400 dark:text-green-300" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-green-800 dark:text-green-200">{successMessage}</p>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400 dark:text-red-300" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Error</h3>
              <p className="text-sm text-red-700 dark:text-red-300 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filter Controls */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className={combineClasses('h-5 w-5', themeClasses.text.muted)} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search agents by name or ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={combineClasses(
                'block w-full pl-10 pr-3 py-2 rounded-md shadow-sm sm:text-sm',
                'border border-gray-300 dark:border-gray-600',
                'bg-white dark:bg-gray-800',
                'text-gray-900 dark:text-white',
                'placeholder-gray-400 dark:placeholder-gray-500',
                'focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400'
              )}
            />
          </div>
        </div>
        <div>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value as 'all' | 'workflow' | 'assistant')}
            className={combineClasses(
              'block w-full px-3 py-2 rounded-md shadow-sm sm:text-sm',
              'border border-gray-300 dark:border-gray-600',
              'bg-white dark:bg-gray-800',
              'text-gray-900 dark:text-white',
              'focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400'
            )}
          >
            <option value="all">All Agents ({totalAgents})</option>
            <option value="workflow">Workflows ({workflowCount})</option>
            <option value="assistant">Assistants ({assistantCount})</option>
          </select>
        </div>
      </div>

      {/* Agents List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className={combineClasses(
            'text-lg',
            themeClasses.text.secondary
          )}>Loading agents...</div>
        </div>
      ) : filteredAgents.length === 0 ? (
        <div className={combineClasses(
          themeClasses.background.secondary,
          themeClasses.border.default,
          'border rounded-md p-8 text-center'
        )}>
          <svg className={combineClasses(
            'mx-auto h-12 w-12',
            themeClasses.text.muted
          )} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
          <h3 className={combineClasses(
            'mt-2 text-sm font-medium',
            themeClasses.text.primary
          )}>
            {searchQuery || filterType !== 'all' ? 'No agents match your search' : 'No agents linked'}
          </h3>
          <p className={combineClasses(
            'mt-1 text-sm',
            themeClasses.text.secondary
          )}>
            {searchQuery || filterType !== 'all'
              ? 'Try adjusting your search or filter criteria.'
              : isAdmin
                ? 'Get started by creating your first agent.'
                : 'No agents have been assigned to you yet. Contact your administrator to link agents to your account.'
            }
          </p>
          {/* Create Agent button - Admin only */}
          {!searchQuery && filterType === 'all' && isAdmin && (
            <div className="mt-6">
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition-colors"
              >
                Create Your First Agent
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {filteredAgents.map((agent) => (
            <UnifiedAgentCard
              key={agent.id}
              agent={agent}
              onDelete={handleDeleteAgent}
            />
          ))}
        </div>
      )}

      {/* Create Agent Modal */}
      <CreateAgentModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
      />
    </div>
  );
};

export default AgentManagementPage;